import { MenuItem } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValueLoadable } from 'recoil';
import * as Yup from 'yup';
import Axios from '../../../common/axiosInterceptor';
import { FormikTextField } from '../../../common/formik-material-ui';
import { trackSaveAddVMDrop } from '../../../common/userTracking';
import { agentUserId } from '../../../recoil/atoms/agentUserId';
import { user } from '../../../recoil/atoms/user';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';
import { RecordNow } from '../recorder/RecordNow';
import { ModalWithStyles } from './ModalWithStyles';

const useStyles = makeStyles((theme) => ({
  header: {
    color: 'var(--primary)', // pull color from primary
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  modal: {
    paddingBottom: 0,
    maxHeight: 'calc(100vh - 210px)',
    overflowY: 'auto',
  },
  defaultAction: {
    marginTop: theme.spacing(2),
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  spinner: {
    position: 'fixed !important',
  },
}));
export function AddVmDropModal({ show, onHide, setRefetchToggleForVmDrop }) {
  const userAtom = useRecoilValueLoadable(user);
  const currentUser = useRecoilValueLoadable(agentUserId);

  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const classes = useStyles();
  const [filenames, setFilenames] = useState({
    voicemail: '',
  });
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [filepaths, setFilepaths] = useState({
    voicemail: '',
  });

  useEffect(() => {
    setFilenames({
      voicemail: '',
    });
    setFilepaths({
      voicemail: '',
    });
    setUploadedFiles({});
  }, [show]);

  const onChangeFile = (type) => ({ target: { files } }) => {
    setFilenames({
      ...filenames,
      // voicemail or greeting
      [type]:
        files.length > 1 ? `${files.length} files` : files?.[0]?.name ?? '',
    });
    setFilepaths({
      ...filepaths,
      [type]: files?.[0] && URL.createObjectURL(files?.[0]), // create file blob
    });
    setUploadedFiles({
      ...uploadedFiles,
      [type]: files,
    }); // take first file
  };
  const handleRecording = (type) => (files) => {
    setUploadedFiles({
      ...uploadedFiles,
      [type]: files,
    });
  };
  useEffect(() => {
    setFilenames({
      voicemail: '', // filename from uri/path
    });
    setFilepaths({
      voicemail: '',
    });
    //   replave with settings seelctor
  }, []);
  const addVmDropSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required('Required'),
        // need to add validation for record now
        // mp3File: Yup.string().required('Required'),
        submitVoicemailMp3File: Yup.object({
          name: Yup.string().checkFileSize(
            20_000_000,
            'File must be at or below 20mb'
          ),
        }),
      }),
    []
  );
  function hideFunction(formik) {
    formik.setFieldValue('mp3File', '');
    onHide();
  }
  return (
    <Formik
      onSubmit={async (values, actions) => {
        if (values.mp3Type === 'record' && uploadedFiles.voicemail) {
          const data = new FormData();
          data.append('webm', uploadedFiles.voicemail[0]);
          data.append('businessid', userAtom.contents.businessid);
          data.append('apikey', userAtom.contents.apikey);
          data.append('userid', currentUser.contents.id);
          data.append('call', 'HandleAudioRecording');
          data.append('id', currentUser.contents.id);
          data.append('iid', 0);
          data.append('name', values.name);
          data.append('type', 'new-agent-voicemail-drop');
          const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
          await Axios.post(url, data, { withCredentials: true });
        }
        if (values.mp3Type === 'mp3' && uploadedFiles.voicemail) {
          const data = new FormData();
          data.append('call', 'submitVMDropMp3File');
          data.append('businessid', userAtom.contents.businessid);
          data.append('apikey', userAtom.contents.apikey);
          data.append('userid', currentUser.contents.id);
          data.append('vmdropName', values.name);
          data.append('mp3', uploadedFiles.voicemail[0]);
          const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
          await Axios.post(url, data, { withCredentials: true });
        }
        enqueueSnackbarWithStyles({
          message: `Successfully Added ${values.name} VM Drop`,
          alertProps: { severity: 'success' },
        });
        onHide();
        setRefetchToggleForVmDrop({});
        actions.resetForm({});
      }}
      validationSchema={addVmDropSchema}
      initialValues={{
        name: '',
        mp3Type: 'mp3',
        mp3File: '',
        submitVoicemailMp3File: {
          name: filenames?.voicemail ?? '',
        },
      }}
    >
      {(formik) => {
        return (
          <>
            <form
              className="form form-label-right"
              onSubmit={() => {
                trackSaveAddVMDrop();
                formik.handleSubmit();
              }}
              noValidate
            >
              <ModalWithStyles
                onSubmit={formik.handleSubmit}
                title="Add VM Drop Message"
                show={show}
                onHide={() => {
                  hideFunction(formik);
                }}
                disableSubmit={
                  !formik.values.name.length || formik.values.mp3Type === 'mp3'
                    ? !formik.values.mp3File.length
                    : typeof uploadedFiles?.voicemail !== 'object'
                }
                isSubmitting={formik.isSubmitting}
                size="sm"
              >
                <main className="col">
                  <div>
                    <FormikTextField
                      name="name"
                      className={classes.field}
                      formik={formik}
                      fullWidth
                      label="Name"
                      size="small"
                      required
                      variant="outlined"
                    />
                  </div>
                  <div>
                    <RecordNow
                      dropdownLabel="Voice Mail Message"
                      dropdownName="mp3Type"
                      dropdownType={formik.values?.mp3Type}
                      outlineLabel="Voice Mail Message"
                      ttsLabel="Voicemail"
                      // ttsName="mp3File"
                      filenameName="mp3File"
                      filesizeName="submitVoicemailMp3File.name"
                      formik={formik}
                      filename={filenames.voicemail}
                      filepath={filepaths.voicemail}
                      onChangeFile={onChangeFile('voicemail')}
                      onRecording={handleRecording('voicemail')}
                    >
                      <MenuItem value="mp3">MP3</MenuItem>
                      <MenuItem value="record">Record Now</MenuItem>
                    </RecordNow>
                  </div>
                </main>
              </ModalWithStyles>
            </form>
          </>
        );
      }}
    </Formik>
  );
}
